import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, FileAudio } from 'lucide-react';
import { toast } from 'sonner';

interface AudioUploaderProps {
  onAudioSelected: (file: File) => void;
}

const AudioUploader = ({ onAudioSelected }: AudioUploaderProps) => {
  const [isDragging, setIsDragging] = useState(false);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file && file.type.startsWith('audio/')) {
      onAudioSelected(file);
    } else {
      toast.error('Please upload an audio file');
    }
  }, [onAudioSelected]);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'audio/*': ['.mp3', '.wav', '.m4a']
    },
    multiple: false,
    onDragEnter: () => setIsDragging(true),
    onDragLeave: () => setIsDragging(false),
  });

  return (
    <div
      {...getRootProps()}
      className={`w-full p-8 border-2 border-dashed rounded-lg cursor-pointer transition-colors duration-200 ${
        isDragging
          ? 'border-primary bg-primary/5'
          : 'border-gray-300 hover:border-primary'
      }`}
    >
      <input {...getInputProps()} />
      <div className="flex flex-col items-center justify-center gap-4">
        <div className="p-4 bg-primary/10 rounded-full">
          {isDragging ? (
            <Upload className="w-8 h-8 text-primary" />
          ) : (
            <FileAudio className="w-8 h-8 text-primary" />
          )}
        </div>
        <div className="text-center">
          <p className="text-lg font-semibold text-gray-700">
            Drop your audio file here
          </p>
          <p className="text-sm text-gray-500">
            or click to select (MP3, WAV, M4A)
          </p>
        </div>
      </div>
    </div>
  );
};

export default AudioUploader;